// src/stores/riskStore.ts
import { proxy } from 'valtio';
import type { Risk } from '../types/risk';
import { api } from '../utils/api';

export type { Risk };

interface RiskState {
  risks: Risk[];
  loading: boolean;
  searchTerm: string;
  categoryFilter: string;
  statusFilter: string;
  treatmentFilter: string;
}

export const riskStore = proxy<RiskState>({
  risks: [],
  loading: true,
  searchTerm: '',
  categoryFilter: 'all',
  statusFilter: 'all',
  treatmentFilter: 'all',
});

export const riskActions = {
  loadRisks: async () => {
    try {
      riskStore.loading = true;
      const data = await api.get('/risks') as Risk[];
      // Backward-compat: build controls array from legacy flat fields if empty
      riskStore.risks = data.map(risk => ({
        ...risk,
        controls: (risk.controls && risk.controls.length > 0)
          ? risk.controls
          : risk.control
            ? [{ control: risk.control, customControl: risk.customControl || '', controlImplementation: risk.controlImplementation || '' }]
            : [],
      }));
    } catch (e) {
      console.error('Failed to load risks from API:', e);
      const stored = localStorage.getItem('risks');
      if (stored) {
        try { riskStore.risks = JSON.parse(stored); } catch {}
      }
    } finally {
      riskStore.loading = false;
    }
  },

  saveRisks: async () => {
    // No-op: individual CRUD operations handle persistence now
  },

  addRisk: async (risk: Risk) => {
    riskStore.risks.push(risk);
    try {
      const serverRisk = await api.post('/risks', risk) as Risk;
      // Replace optimistic entry with server response (has real ID, ref number, timestamps)
      const idx = riskStore.risks.findIndex(r => r.id === risk.id);
      if (idx !== -1) {
        riskStore.risks[idx] = serverRisk;
      }
    } catch (e) {
      const idx = riskStore.risks.findIndex(r => r.id === risk.id);
      if (idx !== -1) riskStore.risks.splice(idx, 1);
      console.error('Failed to save risk to API:', e);
      throw e;
    }
  },

  updateRisk: async (id: string, updates: Partial<Risk>) => {
    const index = riskStore.risks.findIndex(r => r.id === id);
    if (index !== -1) {
      const previous = { ...riskStore.risks[index] };
      // Send the store's current updatedAt so the server can use it for optimistic locking.
      // The server will set the new timestamp — do NOT override updatedAt before sending.
      const payload = { ...riskStore.risks[index], ...updates, updatedAt: previous.updatedAt, lastModified: previous.lastModified };
      // Optimistic local update with a provisional timestamp
      const now = new Date().toISOString();
      riskStore.risks[index] = { ...payload, updatedAt: now, lastModified: now };
      try {
        const serverResponse = await api.put(`/risks/${id}`, payload) as Partial<Risk>;
        // Update with server-confirmed timestamps
        if (serverResponse.updatedAt) {
          riskStore.risks[index] = { ...riskStore.risks[index], updatedAt: serverResponse.updatedAt, lastModified: serverResponse.lastModified || serverResponse.updatedAt };
        }
      } catch (e: unknown) {
        riskStore.risks[index] = previous;
        const err = e as Error & { status?: number };
        if (err.status === 409) {
          // Conflict — reload latest data from server
          console.warn('Conflict detected on risk update, reloading...');
          await riskActions.loadRisks();
        }
        console.error('Failed to update risk on API:', e);
        throw e;
      }
    }
  },

  deleteRisk: async (id: string) => {
    const index = riskStore.risks.findIndex(r => r.id === id);
    if (index !== -1) {
      const removed = riskStore.risks[index];
      riskStore.risks.splice(index, 1);
      try {
        await api.del(`/risks/${id}`);
      } catch (e) {
        riskStore.risks.splice(index, 0, removed);
        console.error('Failed to delete risk from API:', e);
        throw e;
      }
    }
  },

  getRiskById: (id: string): Risk | undefined => {
    return riskStore.risks.find(r => r.id === id);
  },

  generateReferenceNumber: (): string => {
    const year = new Date().getFullYear();
    const existingNumbers = riskStore.risks
      .map(r => r.referenceNumber)
      .filter(ref => ref && ref.startsWith(`R-${year}-`))
      .map(ref => parseInt(ref.split('-')[2]))
      .filter(num => !isNaN(num));

    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    return `R-${year}-${String(nextNumber).padStart(4, '0')}`;
  },

  // Filter actions
  setSearchTerm: (term: string) => { riskStore.searchTerm = term; },
  setCategoryFilter: (category: string) => { riskStore.categoryFilter = category; },
  setStatusFilter: (status: string) => { riskStore.statusFilter = status; },
  setTreatmentFilter: (treatment: string) => { riskStore.treatmentFilter = treatment; },
};

// Helper functions
import { riskMatrixStore } from './riskMatrixStore';

export const getRiskLevel = (likelihood: string, impact: string) => {
  const score = calculateRiskScore(likelihood, impact);
  const likelihoodLabels = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
  const impactLabels = ['Insignificant', 'Minor', 'Moderate', 'Serious', 'Catastrophic'];
  const likelihoodIdx = likelihoodLabels.indexOf(likelihood);
  const impactIdx = impactLabels.indexOf(impact);

  let color: 'green' | 'amber' | 'red' = 'green';
  let level: 'Low' | 'Medium' | 'High' | 'Critical' = 'Low';

  if (likelihoodIdx >= 0 && impactIdx >= 0 &&
      riskMatrixStore.infoSec &&
      riskMatrixStore.infoSec.colors &&
      riskMatrixStore.infoSec.colors[likelihoodIdx]) {
    const matrixColor = riskMatrixStore.infoSec.colors[likelihoodIdx][impactIdx];
    if (matrixColor) { color = matrixColor; }
  } else {
    if (score <= 4) color = 'green';
    else if (score <= 12) color = 'amber';
    else color = 'red';
  }

  let hexColor = '#16a34a';
  if (color === 'green') { level = 'Low'; hexColor = '#10b981'; }
  else if (color === 'amber') { level = 'Medium'; hexColor = '#f59e0b'; }
  else if (color === 'red') { level = score >= 20 ? 'Critical' : 'High'; hexColor = '#ef4444'; }

  return { level, color: hexColor, score };
};

export const calculateRiskScore = (likelihood: string, impact: string): number => {
  const likelihoodMap: Record<string, number> = { 'Rare': 1, 'Unlikely': 2, 'Possible': 3, 'Likely': 4, 'Almost Certain': 5 };
  const impactMap: Record<string, number> = { 'Insignificant': 1, 'Minor': 2, 'Moderate': 3, 'Serious': 4, 'Catastrophic': 5 };
  return (likelihoodMap[likelihood] || 3) * (impactMap[impact] || 3);
};
